import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from 'react-bootstrap';
import { FaCheck, FaTimes, FaUserClock } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import { YButton } from '../../../ui/YButton';
import {
  approveClubJoinRequest,
  declineClubJoinRequest,
  fetchClubJoinRequests,
  joinRequestErrorMessage,
} from '../services/joinRequestService';
import './ClubAdminJoinRequests.css';

function formatWhen(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

export function ClubAdminJoinRequests({ clubId }) {
  const location = useLocation();
  const sectionRef = useRef(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actingId, setActingId] = useState(null);

  const loadRequests = useCallback(async () => {
    if (!clubId) return;
    setLoading(true);
    setError('');
    try {
      const data = await fetchClubJoinRequests(clubId, { status: 'pending' });
      setRequests(Array.isArray(data?.results) ? data.results : []);
    } catch (err) {
      setError(joinRequestErrorMessage(err, 'Could not load join requests.'));
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    if (!location.state?.focusJoinRequests) return;
    const timer = window.setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [location.state?.focusJoinRequests, requests.length]);

  const handleApprove = async (requestId) => {
    setActingId(requestId);
    setError('');
    try {
      await approveClubJoinRequest(clubId, requestId);
      setRequests((prev) => prev.filter((item) => item.id !== requestId));
    } catch (err) {
      setError(joinRequestErrorMessage(err, 'Could not approve request.'));
    } finally {
      setActingId(null);
    }
  };

  const handleDecline = async (requestId) => {
    setActingId(requestId);
    setError('');
    try {
      await declineClubJoinRequest(clubId, requestId);
      setRequests((prev) => prev.filter((item) => item.id !== requestId));
    } catch (err) {
      setError(joinRequestErrorMessage(err, 'Could not decline request.'));
    } finally {
      setActingId(null);
    }
  };

  return (
    <div
      ref={sectionRef}
      className="club-admin-join-requests"
      data-testid="club-admin-join-requests"
    >
      <div className="club-admin-card__header-left mb-3">
        <FaUserClock aria-hidden />
        <h3 className="club-admin-card__title">Join requests</h3>
      </div>
      <p className="club-admin-hint">
        Requests from Find a Club seekers. Approve to add them as members.
      </p>
      {error ? (
        <p className="club-admin-join-requests__error" role="alert">
          {error}
        </p>
      ) : null}
      {loading ? (
        <div className="club-admin-join-requests__loading">
          <Spinner animation="border" size="sm" role="status" />
          <span>Loading requests…</span>
        </div>
      ) : requests.length === 0 ? (
        <p className="club-admin-hint mb-0">No pending requests.</p>
      ) : (
        <ul className="club-admin-join-requests__list">
          {requests.map((item) => {
            const busy = actingId === item.id;
            return (
              <li key={item.id} className="club-admin-join-requests__item" data-testid={`join-request-${item.id}`}>
                <div className="club-admin-join-requests__meta">
                  <strong>{item.requester_name || item.requester_username}</strong>
                  {item.created_at ? (
                    <span className="club-admin-join-requests__when">{formatWhen(item.created_at)}</span>
                  ) : null}
                </div>
                {item.title ? <p className="club-admin-join-requests__title">{item.title}</p> : null}
                <p className="club-admin-join-requests__message">{item.message}</p>
                <div className="club-admin-join-requests__actions">
                  <YButton
                    type="button"
                    variant="primary"
                    size="sm"
                    disabled={busy}
                    onClick={() => handleApprove(item.id)}
                    data-testid={`join-request-approve-${item.id}`}
                  >
                    {busy ? (
                      <Spinner animation="border" size="sm" role="status" />
                    ) : (
                      <>
                        <FaCheck aria-hidden className="me-1" />
                        Approve
                      </>
                    )}
                  </YButton>
                  <YButton
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={busy}
                    onClick={() => handleDecline(item.id)}
                    data-testid={`join-request-decline-${item.id}`}
                  >
                    <FaTimes aria-hidden className="me-1" />
                    Decline
                  </YButton>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

ClubAdminJoinRequests.propTypes = {
  clubId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};
